import React, { useState, useEffect } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { MdCancel } from "react-icons/md";

function TaskResultPanel({ results }) {
  const [expandedTests, setExpandedTests] = useState(new Set());

  let executionResults;

  if (results?.data?.exceptionMessage) {
    try {
      executionResults = JSON.parse(
        results.data.exceptionMessage.replace("TestResultException: ", "")
      );
    } catch (e) {
      executionResults = results.data.exceptionMessage;
    }
  }

  // Auto-expand failed tests on load
  useEffect(() => {
    if (Array.isArray(executionResults)) {
      const failedTests = new Set(
        executionResults.filter(r => !r.pass).map(r => r.order)
      );
      setExpandedTests(failedTests);
    }
  }, [results]);

  const handleClick = (testResultId) => {
    setExpandedTests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testResultId)) {
        newSet.delete(testResultId);
      } else {
        newSet.add(testResultId);
      }
      return newSet;
    });
  };

  if (Array.isArray(executionResults)) {
    return (
      <div className="space-y-2 w-full">
        {executionResults.map((result) => {
          const isExpanded = expandedTests.has(result.order);
          return (
            <div
              key={result.order}
              onClick={() => handleClick(result.order)}
              className="rounded-lg overflow-hidden cursor-pointer transition-all duration-200"
              style={{
                background: 'rgba(39, 39, 42, 0.5)',
                border: `1px solid ${result.pass ? 'rgba(120, 150, 130, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                boxShadow: isExpanded
                  ? `0 4px 12px ${result.pass ? 'rgba(120, 150, 130, 0.15)' : 'rgba(239, 68, 68, 0.15)'}`
                  : 'none',
              }}
            >
              <div
                className="px-4 py-3 flex items-center justify-between"
                style={{
                  background: result.pass
                    ? 'rgba(120, 150, 130, 0.08)'
                    : 'rgba(239, 68, 68, 0.08)',
                }}
              >
                <div className="flex items-center gap-2">
                  {result.pass ? (
                    <FaCheckCircle className="w-3.5 h-3.5" style={{ color: 'rgba(140, 170, 150, 1)' }} />
                  ) : (
                    <MdCancel className="w-3.5 h-3.5 text-red-400" />
                  )}
                  <span className="text-xs font-semibold text-zinc-200">
                    Test {result.order}
                  </span>
                </div>
                <span className="text-zinc-500 text-xs">
                  {isExpanded ? "▼" : "▶"}
                </span>
              </div>

              {isExpanded && (
                <div
                  className="px-4 py-3 text-xs border-t"
                  style={{
                    background: 'rgba(24, 24, 27, 0.6)',
                    borderColor: 'rgba(113, 113, 122, 0.2)',
                  }}
                >
                  {result.scenario === "Problem" ? (
                    <div
                      className="p-3 rounded-lg"
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                      }}
                    >
                      <p className="text-red-300 text-xs leading-relaxed">{result.message}</p>
                    </div>
                  ) : (
                    <>
                      {result.scenario && (
                        <div className="mb-3">
                          <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">
                            Scenario
                          </p>
                          <p className="text-zinc-200 leading-relaxed">{result.scenario}</p>
                        </div>
                      )}
                      {result.message && result.scenario !== "Problem" && (
                        <div>
                          <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">
                            Details
                          </p>
                          <div className="space-y-0.5">
                            {result.message.split("\n").map((line, i) => (
                              <p className="text-zinc-300 leading-relaxed" key={i}>
                                {line}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (results?.data?.compileProblem) {
    return (
      <div
        className="rounded-lg p-4"
        style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <MdCancel className="w-4 h-4 text-red-400" />
          <h3 className="font-bold text-sm text-red-300">Compilation Error</h3>
        </div>
        <p className="text-xs text-red-200/90 leading-relaxed">{results.data.compileProblem}</p>
      </div>
    );
  }

  if (typeof executionResults === "string") {
    // Check if the string indicates an exception or error
    const isError = executionResults.toLowerCase().includes('exception') ||
                    executionResults.toLowerCase().includes('error') ||
                    executionResults.toLowerCase().includes('fail');

    return (
      <div
        className="rounded-lg p-4"
        style={{
          background: isError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(132, 204, 22, 0.1)',
          border: isError ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(132, 204, 22, 0.3)',
        }}
      >
        {/* <h3 className="font-bold text-sm text-lime-300 mb-2">Test Result</h3> */}
        <p className="text-xs text-zinc-100 leading-relaxed">
          {executionResults.replace("TestException: ", "")}
        </p>
      </div>
    );
  }

  if (results === "AUTH ERROR") {
    return (
      <div
        className="rounded-lg p-4"
        style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <MdCancel className="w-4 h-4 text-red-400" />
          <h3 className="font-bold text-sm text-red-300">Auth Error</h3>
        </div>
        <p className="text-xs text-red-200/80 leading-relaxed">
          You've been logged out due to inactivity. Save your work locally and
          login to continue.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg p-4 text-center"
      style={{
        background: 'rgba(63, 63, 70, 0.4)',
        border: '1px solid rgba(113, 113, 122, 0.3)',
      }}
    >
      <p className="text-xs text-zinc-400">Hit the Run button to test your code.</p>
    </div>
  );
}

export default TaskResultPanel;

